// =============================================================================
// FILE: packages/renderer/src/lib/contextAnalyzer.ts
// PURPOSE: "Master Context Ingestor". Deterministically analyzes project
//   text/metadata (Revit exports, requirements PDFs pasted as text) and derives
//   the discovered constraints + a proposed MULTI-TRADE design package
//   (Telecom, Fire Alarm, Electrical, Plumbing, Architecture). Full 3D geometry
//   scanning is the Phase-3 North Star; this MVP proves autonomous intent.
// =============================================================================

export interface DiscoveredContext {
  buildings: number; floors: number; serverRooms: number; workstations: number;
  powerPanels: number; cableCategory: '6' | '6A' | 'unspecified';
  fireDevices: number; plumbingFixtures: number; lightingFixtures: number;
  hasStructural: boolean; hasArchitecture: boolean; hasElectrical: boolean;
  hasFireAlarm: boolean; hasPlumbing: boolean;
}

export interface DesignRecommendation {
  racks: number; drops: number; patchPanels: number; switches: number;
  fiberBackboneFt: number; diagrams: string[];
}

export interface ContextResult { ctx: DiscoveredContext; rec: DesignRecommendation; strategy: string }

// ── Multi-trade model ────────────────────────────────────────────────────────
export type TradeKey = 'telecom' | 'fire_alarm' | 'electrical' | 'plumbing' | 'architecture';

export interface TradePackage {
  key: TradeKey;
  division: string;   // CSI MasterFormat division
  label: string;
  color: string;
  detected: boolean;  // explicitly referenced in the source data
  items: string[];    // scope line items
  estimateUsd: number;
}

export interface MultiTradeResult {
  ctx: DiscoveredContext;
  packages: TradePackage[];
  totalUsd: number;
  timelineWeeks: number;
  diagrams: string[];
  strategy: string;
}

function sumMatches(text: string, re: RegExp): number {
  let total = 0; let m: RegExpExecArray | null;
  const r = new RegExp(re.source, 'gi');
  while ((m = r.exec(text)) !== null) total += parseInt(m[1] ?? '0', 10) || 0;
  return total;
}

export const SAMPLE_PROJECT = `PROJECT: Sierra Manor Campus — Trade Coordination Export
[ARCHITECTURE.rvt] 3 buildings, 9 floors total. 200 workstations across open office areas.
[ELECTRICAL.rvt] 12 power panels / panelboards. Normal + standby power to all IDFs. 340 lighting fixtures.
[STRUCTURAL.rvt] Steel frame; beams at 30ft o.c. — route cabling to avoid structural members.
[FIRE-ALARM.rvt] Addressable FACP. 96 smoke detectors, 40 horn/strobe notification appliances, 12 pull stations.
[PLUMBING.rvt] 64 plumbing fixtures (water closets, lavatories, sinks) across 8 restroom cores.
[REQUIREMENTS.pdf] 4 server rooms (1 MDF + 3 IDF). Category 6A UTP required. 25-year warranty.
Manufacturer preference: CommScope SYSTIMAX. All work per ANSI/TIA-568, NFPA 72, NEC/CEC & BICSI TDMM.`;

export function analyzeProject(text: string): ContextResult {
  const ctx = discover(text);
  const drops = Math.round(ctx.workstations * 1.15);
  const rec: DesignRecommendation = {
    racks: Math.max(ctx.serverRooms, 1),
    drops,
    patchPanels: Math.ceil(drops / 48) || 0,
    switches: Math.ceil(drops / 44) || 0,
    fiberBackboneFt: ctx.serverRooms > 1 ? (ctx.serverRooms - 1) * 350 : 0,
    diagrams: ['System Overview', 'Rack Elevations', 'Riser Diagram', 'Cabling Schedule', 'Div 27 Specification'],
  };
  const strategy =
    `I analyzed the ${[ctx.hasArchitecture && 'Architecture', ctx.hasElectrical && 'Electrical', ctx.hasStructural && 'Structural'].filter(Boolean).join(', ') || 'project'} data. ` +
    `I see ${ctx.serverRooms} server room${ctx.serverRooms === 1 ? '' : 's'} and ${ctx.workstations.toLocaleString()} workstations across ${ctx.buildings} building${ctx.buildings === 1 ? '' : 's'}. ` +
    `Requirements specify ${ctx.cableCategory === 'unspecified' ? 'an unstated cable category' : `Category ${ctx.cableCategory}`}. ` +
    `${ctx.hasStructural ? 'I\u2019ll route cabling to avoid the structural beams. ' : ''}` +
    `Should I generate the Low-Voltage design package now?`;
  return { ctx, rec, strategy };
}

function discover(text: string): DiscoveredContext {
  const t = text || '';
  const serverRooms = sumMatches(t, /(\d+)\s*(?:server rooms?|data ?center|equipment rooms?|telecom rooms?|mdf|idf)/) || (/(mdf|idf|server room)/i.test(t) ? 1 : 0);
  const workstations = sumMatches(t, /(\d+)\s*(?:workstations?|desks?|seats?|work areas?|outlets?)/);
  const floors = sumMatches(t, /(\d+)\s*(?:floors?|stor(?:e?y|ies)|levels?)/);
  const buildings = sumMatches(t, /(\d+)\s*buildings?/) || 1;
  const powerPanels = sumMatches(t, /(\d+)\s*(?:power panels?|electrical panels?|panelboards?)/);
  const lightingFixtures = sumMatches(t, /(\d+)\s*(?:lighting fixtures?|light fixtures?|luminaires?)/);
  const fireDevices = sumMatches(t, /(\d+)\s*(?:smoke detectors?|heat detectors?|horn\/?strobes?|notification appliances?|pull stations?|fire alarm devices?)/);
  const plumbingFixtures = sumMatches(t, /(\d+)\s*(?:plumbing fixtures?|fixtures?|water closets?|lavator(?:y|ies)|sinks?|urinals?)/);
  const cableCategory: DiscoveredContext['cableCategory'] = /cat\s?6a|category\s?6a/i.test(t) ? '6A' : /cat\s?6|category\s?6/i.test(t) ? '6' : 'unspecified';

  return {
    buildings, floors, serverRooms, workstations, powerPanels, cableCategory,
    fireDevices, plumbingFixtures, lightingFixtures,
    hasStructural: /structural|beam|steel frame/i.test(t),
    hasArchitecture: /architect|wall|floor plan|building/i.test(t),
    hasElectrical: /electrical|power panel|panelboard|nec/i.test(t),
    hasFireAlarm: /fire alarm|smoke detector|facp|nfpa\s?72|notification appliance|pull station/i.test(t),
    hasPlumbing: /plumbing|fixture|water closet|lavator|restroom|domestic water/i.test(t),
  };
}

// ── The autonomous multi-trade engineering pass ──────────────────────────────
export function analyzeMultiTrade(text: string): MultiTradeResult {
  const ctx = discover(text);
  const area = Math.max(ctx.buildings * Math.max(ctx.floors, 1) * 12000, ctx.workstations * 250); // rough gsf

  // Estimate counts when a trade is present but not itemized.
  const drops = Math.max(1, Math.round(ctx.workstations * 1.15));
  const racks = Math.max(ctx.serverRooms, 1);
  const fireDevices = ctx.fireDevices || (ctx.hasFireAlarm ? Math.ceil(area / 900) : 0);
  const plumbingFixtures = ctx.plumbingFixtures || (ctx.hasPlumbing ? Math.ceil(ctx.workstations / 12) : 0);
  const lighting = ctx.lightingFixtures || (ctx.hasElectrical ? Math.ceil(area / 80) : 0);
  const panels = ctx.powerPanels || (ctx.hasElectrical ? Math.max(2, ctx.buildings * 3) : 0);

  const packages: TradePackage[] = [
    {
      key: 'telecom', division: 'Div 27 — Communications', label: 'Low-Voltage / Telecom', color: '#2DD4BF',
      detected: true,
      items: [
        `${racks} equipment rack${racks === 1 ? '' : 's'} across ${ctx.serverRooms || 1} telecom room${ctx.serverRooms === 1 ? '' : 's'}`,
        `${drops.toLocaleString()} Category ${ctx.cableCategory === 'unspecified' ? '6A' : ctx.cableCategory} cable drops`,
        `${Math.ceil(drops / 48)} patch panels · ${Math.ceil(drops / 44)} access switches`,
        ctx.serverRooms > 1 ? `${(ctx.serverRooms - 1) * 350} ft OM4 fiber backbone` : 'Single-room copper distribution',
      ],
      estimateUsd: drops * 185 + racks * 4200 + (ctx.serverRooms > 1 ? (ctx.serverRooms - 1) * 350 * 6 : 0),
    },
    {
      key: 'fire_alarm', division: 'Div 28 — Electronic Safety & Security', label: 'Fire Alarm', color: '#F97066',
      detected: ctx.hasFireAlarm,
      items: [
        'Addressable fire alarm control panel (FACP) per NFPA 72',
        `${fireDevices} initiating & notification devices (smoke, pull, horn/strobe)`,
        'Riser diagram, battery calc & sequence of operations',
      ],
      estimateUsd: fireDevices * 145 + (ctx.hasFireAlarm ? 8500 : 0),
    },
    {
      key: 'electrical', division: 'Div 26 — Electrical', label: 'Electrical / Power', color: '#F59E0B',
      detected: ctx.hasElectrical,
      items: [
        `${panels} panelboards (normal + standby)`,
        `${lighting.toLocaleString()} lighting fixtures & controls per NEC/CEC`,
        'Branch circuiting, panel schedules & one-line diagram',
      ],
      estimateUsd: panels * 3800 + lighting * 240 + ctx.workstations * 180,
    },
    {
      key: 'plumbing', division: 'Div 22 — Plumbing', label: 'Plumbing', color: '#38BDF8',
      detected: ctx.hasPlumbing,
      items: [
        `${plumbingFixtures} plumbing fixtures (WC, lav, sink, urinal)`,
        'Domestic water, sanitary & vent riser diagrams',
        'Fixture schedule & connection details',
      ],
      estimateUsd: plumbingFixtures * 950 + (ctx.hasPlumbing ? 6500 : 0),
    },
    {
      key: 'architecture', division: 'Base — Architecture', label: 'Architecture', color: '#A78BFA',
      detected: ctx.hasArchitecture,
      items: [
        `${ctx.buildings} building${ctx.buildings === 1 ? '' : 's'} · ${Math.max(ctx.floors, 1)} floor${ctx.floors === 1 ? '' : 's'} · ~${area.toLocaleString()} gsf`,
        'Coordinated floor plans, RCP & finish schedule',
        '3D isometric renderings (people · vehicles · landscaping) — image-gen ready',
      ],
      estimateUsd: Math.round(area * 0.9),
    },
  ];

  const active = packages.filter((p) => p.detected);
  const totalUsd = active.reduce((s, p) => s + p.estimateUsd, 0);
  const timelineWeeks = Math.max(3, Math.round(active.length * 1.5 + ctx.buildings + ctx.floors / 3));

  const diagrams = [
    'Trade Coordination Matrix', 'System Overview (all trades)', 'Telecom Riser & Rack Elevations',
    ...(ctx.hasFireAlarm ? ['Fire Alarm Riser (NFPA 72)'] : []),
    ...(ctx.hasElectrical ? ['Electrical One-Line'] : []),
    ...(ctx.hasPlumbing ? ['Plumbing Riser'] : []),
    'CSI MasterFormat Specification Set',
  ];

  const tradeNames = active.map((p) => p.label).join(', ');
  const strategy =
    `I ingested the project data and identified a ${active.length}-trade scope: ${tradeNames}. ` +
    `The campus spans ${ctx.buildings} building${ctx.buildings === 1 ? '' : 's'}, ~${Math.max(ctx.floors, 1)} floor${ctx.floors === 1 ? '' : 's'} and roughly ${area.toLocaleString()} gsf. ` +
    `${ctx.hasStructural ? 'I will coordinate all routing to clear the steel structural grid. ' : ''}` +
    `Preliminary design fee across all trades is about $${totalUsd.toLocaleString()}, delivered over ~${timelineWeeks} weeks. ` +
    `I can generate the complete Multi-Trade Design Package — coordinated drawings, schedules and a CSI specification — as RCD² engineer of record. Approve to generate.`;

  return { ctx, packages, totalUsd, timelineWeeks, diagrams, strategy };
}
